/* Veil — cookie consent.
   Branded banner that gates all NON-ESSENTIAL data collection behind an
   explicit choice. Essential cookies (cart/session) are always allowed.
   Nothing analytics/marketing may run until VeilConsent.granted(cat) is true.

   Wired to Shopify's Customer Privacy API: every choice is passed to
   Shopify.customerPrivacy.setTrackingConsent(), which is what actually switches
   Shopify's own analytics / marketing tracking on or off. The banner only
   appears where Shopify says consent is required (shouldShowBanner(), set by
   the regions in Settings > Customer privacy), so Shopify's built-in banner must
   stay HIDDEN or visitors would see two. localStorage remains as a fallback
   for when the API isn't available (e.g. the theme editor). */
(() => {
  const KEY = 'veil_consent';
  const MAX_AGE = 180 * 24 * 60 * 60 * 1000;   // re-ask after ~6 months
  const read = () => {
    try {
      const s = JSON.parse(localStorage.getItem(KEY));
      if (s && s.ts && Date.now() - s.ts > MAX_AGE) return null;   // expired → ask again
      return s;
    } catch { return null; }
  };
  const subs = [];

  let state = read();  // null = no choice yet → nothing non-essential may run

  const api = window.VeilConsent = {
    get: () => state && Object.assign({}, state),
    granted: (cat) => !!(state && state[cat]),   // gate for analytics/marketing scripts
    onChange: (cb) => { subs.push(cb); if (state) cb(state); },
    open: () => show(),
    set(analytics, marketing) { persist({ essential: true, analytics: !!analytics, marketing: !!marketing }); }
  };

  /* ---- Shopify Customer Privacy API ---- */
  const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
  function tellShopify(s) {
    const cp = privacy();
    if (!cp || !s) return;
    try {
      cp.setTrackingConsent({
        analytics: !!s.analytics,
        marketing: !!s.marketing,
        preferences: !!s.analytics,
        sale_of_data: !!s.marketing
      }, () => {});
    } catch (e) {}
  }
  function withPrivacyApi(cb) {
    if (privacy()) return cb(privacy());
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }],
          (err) => cb(err ? null : privacy()));
        return;
      } catch (e) {}
    }
    cb(null);
  }

  function persist(next) {
    state = Object.assign({}, next, { ts: Date.now() });
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
    tellShopify(state);
    subs.forEach((cb) => { try { cb(state); } catch {} });
    hide();
  }

  /* ---- banner UI ---- */
  const el = document.createElement('div');
  el.className = 'consent';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Cookie choices');
  // organic wax-blob edge (deterministic, so it's stable across renders)
  const blob = (cx, cy, r, lobes, amp) => {
    const p = [];
    for (let i = 0; i < lobes; i++) {
      const a = (i / lobes) * Math.PI * 2;
      const k = Math.sin(i * 1.7 + 0.6) + 0.6 * Math.sin(i * 3.1 + 1.9) + 0.45 * Math.sin(i * 5.3 + 0.4);
      const rr = r * (1 + amp * k);
      p.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
    const n = p.length;
    let d = 'M ' + p[0][0].toFixed(1) + ' ' + p[0][1].toFixed(1) + ' ';
    for (let i = 0; i < n; i++) {
      const p0 = p[(i - 1 + n) % n], p1 = p[i], p2 = p[(i + 1) % n], p3 = p[(i + 2) % n];
      const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += 'C ' + c1x.toFixed(1) + ' ' + c1y.toFixed(1) + ' ' + c2x.toFixed(1) + ' ' + c2y.toFixed(1) + ' ' + p2[0].toFixed(1) + ' ' + p2[1].toFixed(1) + ' ';
    }
    return d + 'Z';
  };
  const waxD = blob(160, 160, 136, 13, 0.062);

  const SEAL =
    '<svg class="consent__svg" viewBox="0 0 320 320" role="img" aria-label="Wax seal">' +
      '<defs>' +
        /* ring-bright: dark centre (the depression), bright mid (the raised rim), dark outer edge */
        '<radialGradient id="veilWax" cx="47%" cy="41%" r="62%">' +
          '<stop offset="0" stop-color="#4a121b"/><stop offset="40%" stop-color="#7c2731"/>' +
          '<stop offset="60%" stop-color="#ac3d4a"/><stop offset="75%" stop-color="#6f2029"/>' +
          '<stop offset="100%" stop-color="#260810"/>' +
        '</radialGradient>' +
        /* sunken face: shadowed at top, catches light toward the bottom */
        '<radialGradient id="veilDisc" cx="50%" cy="48%" r="60%">' +
          '<stop offset="0" stop-color="#63222b"/><stop offset="100%" stop-color="#4e1921"/>' +
        '</radialGradient>' +
        /* concave bevel: dark at top edge, light where the far wall catches light */
        '<linearGradient id="veilBevel" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0" stop-color="#170610"/><stop offset="0.5" stop-color="#531821"/><stop offset="1" stop-color="#c67883"/>' +
        '</linearGradient>' +
        '<radialGradient id="veilGloss" cx="50%" cy="50%" r="50%">' +
          '<stop offset="0" stop-color="#fff" stop-opacity="0.34"/><stop offset="100%" stop-color="#fff" stop-opacity="0"/>' +
        '</radialGradient>' +
        '<radialGradient id="veilShadow" cx="50%" cy="50%" r="50%">' +
          '<stop offset="0" stop-color="#150610" stop-opacity="0.62"/><stop offset="100%" stop-color="#150610" stop-opacity="0"/>' +
        '</radialGradient>' +
        '<radialGradient id="veilGlow" cx="50%" cy="50%" r="50%">' +
          '<stop offset="0" stop-color="#ff9d5c" stop-opacity="0.4"/><stop offset="100%" stop-color="#ff9d5c" stop-opacity="0"/>' +
        '</radialGradient>' +
        '<filter id="veilWaxSh" x="-40%" y="-40%" width="180%" height="180%">' +
          '<feDropShadow dx="0" dy="9" stdDeviation="11" flood-color="#000" flood-opacity="0.6"/>' +
        '</filter>' +
      '</defs>' +
      '<path d="' + waxD + '" fill="url(#veilWax)" filter="url(#veilWaxSh)"/>' +        /* wax body, raised-rim shading */
      '<ellipse cx="150" cy="64" rx="98" ry="26" fill="url(#veilGloss)"/>' +            /* highlight along the top of the raised rim */
      '<ellipse cx="160" cy="256" rx="98" ry="30" fill="url(#veilGlow)"/>' +           /* warm reflection, bottom rim */
      '<circle cx="160" cy="160" r="108" fill="url(#veilDisc)" stroke="url(#veilBevel)" stroke-width="5"/>' + /* flat, uniform sunken floor + concave bevel wall */
    '</svg>';

  el.innerHTML =
    '<div class="consent__seal">' + SEAL +
      '<div class="consent__face">' +
        '<p class="consent__title">Veil</p>' +
        '<p class="consent__text">A few cookies keep Veil working — a couple more, only if you allow. <a href="/policies/privacy-policy">Policy</a>.</p>' +
        '<div class="consent__prefs" hidden>' +
          '<label class="consent__toggle"><input type="checkbox" data-cat="analytics"><span>Analytics<small>How the site is used</small></span></label>' +
          '<label class="consent__toggle"><input type="checkbox" data-cat="marketing"><span>Marketing<small>Drops &amp; offers you’ll love</small></span></label>' +
        '</div>' +
        '<div class="consent__actions">' +
          '<div class="consent__btns">' +
            '<button class="btn btn--ghost" type="button" data-consent="essential">Essentials</button>' +
            '<button class="btn btn--solid" type="button" data-consent="all">Accept all</button>' +
            '<button class="btn btn--solid" type="button" data-consent="save" hidden>Save</button>' +
          '</div>' +
          '<button class="consent__link" type="button" data-consent="choose">Customise</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  document.body.appendChild(el);

  const prefs = el.querySelector('.consent__prefs');
  const textEl = el.querySelector('.consent__text');
  const allBtn = el.querySelector('[data-consent="all"]');
  const saveBtn = el.querySelector('[data-consent="save"]');

  // Never show two banners: if Shopify's own cookie banner is on the page, ours steps aside.
  const shopifyBanner = () => document.querySelector('#shopify-pc__banner, [id^="shopify-pc__banner"], .shopify-pc__banner__dialog');
  const show = () => { if (!shopifyBanner()) el.classList.add('is-open'); };
  try {
    new MutationObserver(() => { if (shopifyBanner()) el.classList.remove('is-open'); })
      .observe(document.body, { childList: true, subtree: true });
  } catch (e) {}
  const hide = () => { el.classList.remove('is-open'); };

  el.addEventListener('click', (e) => {
    const act = e.target.closest('[data-consent]')?.dataset.consent;
    if (!act) return;
    if (act === 'choose') {                    // reveal granular toggles; drop the blurb to make room
      prefs.hidden = false; allBtn.hidden = true; saveBtn.hidden = false;
      textEl.hidden = true;
      e.target.setAttribute('hidden', '');
    } else if (act === 'essential') {
      api.set(false, false);
    } else if (act === 'all') {
      api.set(true, true);
    } else if (act === 'save') {
      api.set(prefs.querySelector('[data-cat="analytics"]').checked,
              prefs.querySelector('[data-cat="marketing"]').checked);
    }
  });

  // First visit only, and only where consent is legally required (UK/EU etc., per the
  // regions set in Shopify). A choice already made → stay hidden, but re-sync it to Shopify.
  withPrivacyApi((cp) => {
    if (state) { tellShopify(state); return; }
    let needed = true;
    try { if (cp && typeof cp.shouldShowBanner === 'function') needed = cp.shouldShowBanner(); } catch (e) {}
    if (needed) setTimeout(show, 700);  // give Shopify's banner a moment to appear first (see shopifyBanner)
  });

  // Let visitors change their mind: add a "Cookie settings" link into the footer bar.
  const injectSettings = () => {
    document.querySelectorAll('.footer__base-links').forEach((nav) => {
      if (nav.querySelector('.consent-reopen')) return;
      const a = document.createElement('a');
      a.href = '#'; a.className = 'consent-reopen'; a.textContent = 'Cookie settings';
      a.addEventListener('click', (e) => { e.preventDefault(); show(); });
      nav.appendChild(a);
    });
  };
  injectSettings();

  /* ---- where tracking gets switched on, ONLY after consent ----
     Example (kept inert until real pixels exist):
     api.onChange((s) => {
       if (s.analytics) loadAnalytics();   // e.g. Shopify analytics / GA
       if (s.marketing) loadPixels();      // e.g. TikTok / Meta pixel
     });
     Nothing here runs until the visitor chooses. */
})();
